import { AddOn, AddOnDuration, Pricing } from "../business-case/calculator";

export type PlanChoice = "pilot" | "annual";

export type EstimatorState = {
  plan: PlanChoice;
  mentees: number;
  mentors: number;
  level: string;
  addOns: string[];
  // Chosen support term, in months, for each add-on the buyer picks a duration for.
  durations: Record<string, number>;
};

export type SupportChoice = { key: string; customised: boolean };

export type EstimateLine = { key: string; label: string; amount: number | null };

export type Estimate = {
  plan: PlanChoice;
  participants: number;
  months: number;
  lines: EstimateLine[];
  knownTotal: number;
  hasCustom: boolean;
  // True only when the platform licence itself is unpriced, which is the one case with no
  // meaningful total to show. A service quoted on request leaves the rest of the estimate standing.
  planCustom: boolean;
};

// What a line without a price says, on the add-on card and in the estimate alike.
export const ON_REQUEST = "On request";

const groupsFor = (mentors: number, perGroup: number) => Math.max(1, Math.ceil(mentors / perGroup));

export function durationFor(addOn: AddOn, months: number | undefined): AddOnDuration | null {
  const options = addOn.durations ?? [];

  return options.find((option) => option.months === months) ?? options[0] ?? null;
}

// Mirrors BusinessCase::Pricing. Every amount comes from the `pricing` prop, so this decides how the
// pieces combine and never what any of them costs.
export function buildEstimate(state: EstimatorState, pricing: Pricing): Estimate {
  const participants = Math.max(0, state.mentees) + Math.max(0, state.mentors);

  // A pilot is one fixed cohort. Above its cap the only honest quote is the annual plan.
  const plan: PlanChoice = participants > pricing.pilot.maxParticipants ? "annual" : state.plan;
  const months = plan === "pilot" ? pricing.pilot.months : pricing.annualMonths;

  const lines: EstimateLine[] = [];

  if (plan === "pilot") {
    lines.push({
      key: "pilot",
      label: `Six-month platform pilot · up to ${pricing.pilot.maxParticipants} participants`,
      amount: pricing.pilot.price,
    });
  } else {
    const tier =
      pricing.annualTiers.find(
        (candidate) => candidate.maxParticipants !== null && participants <= candidate.maxParticipants
      ) ?? pricing.annualTiers[pricing.annualTiers.length - 1];

    lines.push({
      key: "annual",
      label:
        tier.maxParticipants === null
          ? "Annual platform licence · more than 200 participants"
          : `Annual platform licence · up to ${tier.maxParticipants} participants`,
      amount: tier.annualPrice,
    });
    lines.push({
      key: "setup",
      label: "One-time setup and administrator onboarding",
      amount: pricing.setupFee,
    });
  }

  const planCustom = lines[0].amount === null;

  pricing.addOns
    .filter((addOn) => state.addOns.includes(addOn.key))
    .forEach((addOn) => {
      if (addOn.unit === "per_mentor_group") {
        const groups = groupsFor(state.mentors, pricing.mentorsPerTrainingGroup);

        lines.push({
          key: addOn.key,
          label: `${addOn.name} · ${groups} ${groups === 1 ? "group" : "groups"}`,
          amount: addOn.price === null ? null : addOn.price * groups,
        });
        return;
      }

      if (addOn.unit === "duration") {
        const duration = durationFor(addOn, state.durations[addOn.key]);

        lines.push({
          key: addOn.key,
          label: duration ? `${addOn.name} · ${duration.label}` : addOn.name,
          amount: duration ? duration.price : addOn.price,
        });
        return;
      }

      lines.push({ key: addOn.key, label: addOn.name, amount: addOn.price });
    });

  return {
    plan,
    participants,
    months,
    lines,
    knownTotal: lines.reduce((total, line) => total + (line.amount ?? 0), 0),
    hasCustom: lines.some((line) => line.amount === null),
    planCustom,
  };
}

// The chosen level is the starting point, not a lock: it stays selected while someone adds or
// removes services below, and only moves if their selection happens to match another level exactly.
export function resolveSupportLevel(state: EstimatorState, pricing: Pricing): SupportChoice {
  const selected = [...state.addOns].sort().join(",");

  const match = pricing.supportLevels.find(
    (level) => [...level.addOns].sort().join(",") === selected
  );

  return match ? { key: match.key, customised: false } : { key: state.level, customised: true };
}

export function initialEstimatorState(pricing: Pricing): EstimatorState {
  const level = pricing.supportLevels[0];

  const durations: Record<string, number> = {};

  pricing.addOns.forEach((addOn) => {
    const fallback = durationFor(addOn, addOn.defaultMonths);

    if (fallback) durations[addOn.key] = fallback.months;
  });

  return {
    plan: "pilot",
    mentees: 15,
    mentors: 15,
    level: level?.key ?? "",
    addOns: [...(level?.addOns ?? [])],
    durations,
  };
}
